/**
 * Ticket attachments (PRD NFR "Attachments"): shapes, limits and the type allowlist both sides
 * use. The server re-checks every file by its magic bytes; the client checks the extension and
 * size first so people get a clear message before anything uploads.
 */
import { z } from 'zod'

/** Files per upload request (one "ticket action"). */
export const MAX_FILES_PER_UPLOAD = 10
/** Default per-file limit; the server reads `MAX_UPLOAD_MB`. */
export const DEFAULT_MAX_UPLOAD_MB = 10
export const MAX_FILENAME_LENGTH = 200

export type AttachmentKind = 'image' | 'pdf' | 'doc' | 'sheet' | 'slides' | 'text'

/** Allowed extensions → canonical MIME type and a kind for the icon. No SVG, HTML or archives. */
export const ATTACHMENT_TYPES: Record<string, { mime: string; kind: AttachmentKind }> = {
  png: { mime: 'image/png', kind: 'image' },
  jpg: { mime: 'image/jpeg', kind: 'image' },
  jpeg: { mime: 'image/jpeg', kind: 'image' },
  gif: { mime: 'image/gif', kind: 'image' },
  webp: { mime: 'image/webp', kind: 'image' },
  heic: { mime: 'image/heic', kind: 'image' },
  heif: { mime: 'image/heif', kind: 'image' },
  pdf: { mime: 'application/pdf', kind: 'pdf' },
  doc: { mime: 'application/msword', kind: 'doc' },
  docx: {
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    kind: 'doc',
  },
  odt: { mime: 'application/vnd.oasis.opendocument.text', kind: 'doc' },
  xls: { mime: 'application/vnd.ms-excel', kind: 'sheet' },
  xlsx: {
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    kind: 'sheet',
  },
  ods: { mime: 'application/vnd.oasis.opendocument.spreadsheet', kind: 'sheet' },
  ppt: { mime: 'application/vnd.ms-powerpoint', kind: 'slides' },
  pptx: {
    mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    kind: 'slides',
  },
  odp: { mime: 'application/vnd.oasis.opendocument.presentation', kind: 'slides' },
  txt: { mime: 'text/plain', kind: 'text' },
  csv: { mime: 'text/csv', kind: 'text' },
}

/** For `<input accept>`. */
export const ATTACHMENT_ACCEPT = Object.keys(ATTACHMENT_TYPES)
  .map((e) => `.${e}`)
  .join(',')

export const ATTACHMENT_TYPES_TEXT = 'images, PDFs, Word, Excel, PowerPoint, text or CSV files'

export function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase()
}

export function attachmentKind(nameOrMime: string): AttachmentKind | 'other' {
  const byExt = ATTACHMENT_TYPES[fileExtension(nameOrMime)]
  if (byExt) return byExt.kind
  const byMime = Object.values(ATTACHMENT_TYPES).find((t) => t.mime === nameOrMime)
  return byMime?.kind ?? 'other'
}

/** "512 B", "14 KB", "2.4 MB" (DESIGN.md §3.5). */
export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  const mb = n / (1024 * 1024)
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`
}

export const ATTACHMENT_MESSAGES = {
  tooMany: `Attach up to ${MAX_FILES_PER_UPLOAD} files at a time.`,
  empty: (name: string) => `“${name}” is empty.`,
  tooBig: (name: string, maxMb: number) => `“${name}” is over the ${maxMb} MB limit.`,
  badType: (name: string) =>
    `“${name}” isn’t a supported file type. Attach ${ATTACHMENT_TYPES_TEXT}.`,
  mismatch: (name: string) =>
    `“${name}” doesn’t look like the file type its name says. Save it again and retry.`,
  infected: (name: string) => `“${name}” was blocked by the virus scanner.`,
} as const

/** Client-side pre-check (name + size). The server re-checks the content. */
export function checkAttachment(
  file: { name: string; size: number },
  maxBytes: number,
): string | null {
  if (!ATTACHMENT_TYPES[fileExtension(file.name)]) return ATTACHMENT_MESSAGES.badType(file.name)
  if (file.size === 0) return ATTACHMENT_MESSAGES.empty(file.name)
  if (file.size > maxBytes)
    return ATTACHMENT_MESSAGES.tooBig(file.name, Math.round(maxBytes / (1024 * 1024)))
  return null
}

/**
 * A display-safe original file name: no path, no control characters, trimmed, at most
 * `MAX_FILENAME_LENGTH` characters (keeping the extension). Never used on disk.
 */
export function cleanFileName(raw: string): string {
  const base = raw.split(/[\\/]/).pop() ?? ''
  // biome-ignore lint/suspicious/noControlCharactersInRegex: stripping control characters
  let name = base.replace(/[\u0000-\u001f\u007f‎‏‪-‮]/g, '').trim()
  if (!name || name === '.' || name === '..') name = 'file'
  if (name.length > MAX_FILENAME_LENGTH) {
    const ext = fileExtension(name)
    const keep = ext && ext.length < 12 ? `.${ext}` : ''
    name = name.slice(0, MAX_FILENAME_LENGTH - keep.length) + keep
  }
  return name
}

export const attachmentSchema = z.object({
  id: z.number().int(),
  ticketId: z.number().int(),
  uploaderId: z.string(),
  /** Reserved for files attached to a comment; ticket-level uploads leave it null. */
  commentId: z.number().int().nullable(),
  name: z.string(),
  mime: z.string(),
  size: z.number().int(),
  createdAt: z.number(),
})
export type AttachmentDto = z.infer<typeof attachmentSchema>

/** `GET /api/attachments/limits` */
export const attachmentLimitsSchema = z.object({
  maxFileBytes: z.number().int(),
  maxFiles: z.number().int(),
})
export type AttachmentLimits = z.infer<typeof attachmentLimitsSchema>

/** Download URL for an attachment (the server checks ticket visibility). */
export const attachmentUrl = (id: number) => `/api/attachments/${id}`
